import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import React from "react";
import { PageHero, Section } from "../../components/Page";
import { SubNav, ABOUT_NAV } from "../../components/SubNav";
import { LeadershipTimeline } from "../../components/LeadershipTimeline";
import { useApi } from "../../hooks/useApi";
import { Mail, User } from "lucide-react";
import { Lightbox } from "../../components/Lightbox";
import { useLang } from "../../i18n/LanguageContext";

export function Leadership() {
  const { t } = useLang();
  const { data } = useApi("/governance/officers");
  const officers = (data || []).filter((o) => !o.is_trustee);
  const trustees = (data || []).filter((o) => o.is_trustee);
  const Card = (o) => (
    <div key={o.id} className="card-hover glass rounded-2xl p-6 text-center" data-testid={`officer-${o.id}`}>
      <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-gold bg-gradient-to-br from-crimson to-palm text-parchment">
        {o.photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${o.photo_url}`} alt={o.name} className="h-full w-full object-cover" /> : <User size={34} />}
      </div>
      <h3 className="mt-4 font-serif-display text-xl font-bold text-crimson">{o.name}</h3>
      <p className="eyebrow mt-1 text-gold">{o.title}</p>
      {o.bio && <p className="mt-2 text-xs text-muted-foreground">{o.bio}</p>}
    </div>
  );
  return (
    <div data-testid="leadership-page">
      <SubNav items={ABOUT_NAV} testId="about-subnav" />
      <PageHero eyebrow="Governance" title="Our Leadership"
        subtitle="The newly elected Executive Board who steward our association. Ofie! Biakoye!"
        image="https://images.unsplash.com/photo-1737649507334-92c9fa4beb7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <Section>
        <h2 className="font-serif-display text-3xl font-bold text-crimson">{t("Executive Board")}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{officers.map(Card)}</div>
        {trustees.length > 0 ? <>
          <h2 className="mt-16 font-serif-display text-3xl font-bold text-crimson">{t("Board of Trustees (Mpanyimfo)")}</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">{t("Our elders who safeguard the association's assets, mediate disputes and guide the executive.")}</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="trustees-grid">{trustees.map(Card)}</div>
        </> : (
          <div className="mt-16 rounded-2xl border border-dashed border-gold/50 bg-parchment-card p-8 text-center" data-testid="trustees-empty">
            <h2 className="font-serif-display text-3xl font-bold text-crimson">{t("Board of Trustees (Mpanyimfo)")}</h2>
            <p className="mt-2 text-muted-foreground">{t("Trustee appointments will be announced here.")}</p>
          </div>
        )}
        <LeadershipTimeline />
      </Section>
    </div>
  );
}

export function Chapters() {
  const { t } = useLang();
  const { data } = useApi("/chapters");
  const chapters = (data || []).filter((c) => c.kind !== "sister");
  const sisters = (data || []).filter((c) => c.kind === "sister");
  return (
    <div data-testid="chapters-page">
      <PageHero eyebrow="OKUSACA Network" title="Chapters & Affiliates"
        subtitle="OKUSACA — the Okuapemman, Asuogya and Akwamu union in North America — currently has 11 member Chapters and 2 Affiliates, working in tandem with our sister organization Okuapemman Europe." />
      <Section>
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-2xl text-muted-foreground">{t("Chapters across the United States and Canada, each led by a Chapter President. Massachusetts is proud to stand among them.")}</p>
          <a href="https://www.okusaca.com/projects-6" target="_blank" rel="noreferrer" className="rounded-full border border-crimson px-4 py-2 text-sm font-semibold text-crimson hover:bg-crimson hover:text-parchment" data-testid="okusaca-link">{t("View on okusaca.com")} ↗</a>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {chapters.map((c) => (
            <div key={c.id} className={`card-hover rounded-2xl p-6 ${c.primary ? "bg-crimson text-parchment" : "glass"}`} data-testid={`chapter-${c.id}`}>
              <span className={`eyebrow ${c.primary ? "text-gold" : "text-gold"}`}>{c.primary ? t("Our Chapter") : t("OKUSACA Chapter")}</span>
              <h3 className="mt-1 font-serif-display text-xl font-bold">{c.name}</h3>
              <p className={`mt-1 text-sm ${c.primary ? "text-parchment/80" : "text-muted-foreground"}`}>{c.region}</p>
              {c.president && <p className={`mt-3 text-sm ${c.primary ? "text-parchment" : ""}`}><span className={`text-xs uppercase tracking-wider ${c.primary ? "text-gold" : "text-palm"}`}>{t("President")}</span><br /><span className="font-semibold">{c.president}</span></p>}
            </div>
          ))}
        </div>
        {sisters.length > 0 && (
          <div className="mt-12">
            <h2 className="font-serif-display text-2xl font-bold text-crimson">{t("Sister Organization")}</h2>
            <div className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {sisters.map((c) => (
                <div key={c.id} className="glass rounded-2xl border-dashed p-6" data-testid={`chapter-${c.id}`}>
                  <span className="eyebrow text-palm">{t("Sister organization")}</span>
                  <h3 className="mt-1 font-serif-display text-xl font-bold">{c.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{c.region}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-12 rounded-2xl bg-gold-light p-6 text-sm text-crimson">
          <strong>{t("Starting a chapter?")}</strong> {t("People of Akuapem, Asuogya or Akwamu descent in areas without a chapter are encouraged to contact the OKUSACA National Secretariat to form an affiliate association and grow into full Chapter membership.")}
        </div>
      </Section>
    </div>
  );
}

export function Welfare() {
  const { t } = useLang();
  const { data } = useApi("/welfare/benefits");
  return (
    <div data-testid="welfare-public-page">
      <PageHero eyebrow="Care & Community" title="Welfare Benefits"
        subtitle="We stand with our members in times of joy and sorrow. Members in good standing for over one year are eligible."
        image="https://images.unsplash.com/photo-1660675134062-7d3bbb340608?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <Section>
        <div className="grid gap-6 md:grid-cols-2">
          {(data || []).map((b) => (
            <div key={b.id} className="card-hover glass rounded-2xl p-6" data-testid={`benefit-${b.id}`}>
              <div className="flex items-center justify-between">
                <span className="eyebrow text-gold">{b.category}</span>
                <span className="rounded-full bg-palm/10 px-2 py-0.5 text-xs text-palm">{t("Members in good standing")}</span>
              </div>
              <h3 className="mt-2 font-serif-display text-2xl font-bold text-crimson">{b.name}</h3>
              <p className="mt-2 text-sm text-foreground/75">{b.description}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function Gallery() {
  const { t } = useLang();
  const { data } = useApi("/gallery");
  const [lightbox, setLightbox] = React.useState(null);
  const [album, setAlbum] = React.useState("__all");
  const src = (u) => (u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  const albums = [...new Set((data || []).map((g) => g.album).filter(Boolean))];
  const shown = (data || []).filter((g) => album === "__all" || g.album === album);
  const groups = album === "__all" ? [...albums.map((a) => [a, shown.filter((g) => g.album === a)]), ["", shown.filter((g) => !g.album)]].filter(([, items]) => items.length) : [[album, shown]];
  return (
    <div data-testid="gallery-page">
      <PageHero eyebrow="Moments" title="Gallery"
        subtitle="Festivals, meetings, and celebrations through the years." />
      <Section>
        {data && data.length === 0 && <p className="text-muted-foreground">{t("Photos from our next festival will appear here soon.")}</p>}
        {albums.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="gallery-albums">
            <button onClick={() => setAlbum("__all")} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${album === "__all" ? "border-crimson bg-crimson text-parchment" : "border-gold/50 hover:bg-gold-light"}`} data-testid="gallery-album-all">{t("All albums")}</button>
            {albums.map((a) => <button key={a} onClick={() => setAlbum(a)} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${album === a ? "border-crimson bg-crimson text-parchment" : "border-gold/50 hover:bg-gold-light"}`} data-testid={`gallery-album-${a.replace(/\W+/g, "-")}`}>{a} <span className="opacity-70">({data.filter((g) => g.album === a).length})</span></button>)}
          </div>
        )}
        {groups.map(([name, items]) => (
          <section key={name || "__none"} className="mb-10" data-testid={`gallery-group-${(name || "other").replace(/\W+/g, "-")}`}>
            {(name || albums.length > 0) && <h3 className="font-serif-display mb-4 text-2xl font-bold text-crimson">{name || t("More moments")} <span className="font-mono-accent text-sm text-muted-foreground">({items.length})</span></h3>}
            <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
              {items.map((g) => (
                <figure key={g.id} className="mb-4 break-inside-avoid overflow-hidden rounded-2xl shadow-md card-hover" data-testid={`gallery-img-${g.id}`}>
                  <button type="button" onClick={() => setLightbox({ items, index: items.indexOf(g) })} className="block w-full cursor-zoom-in" aria-label="View photo full screen" data-testid={`gallery-open-${g.id}`}>
                    <img src={src(g.url)} alt={g.caption || g.album || "Gallery"} className="w-full object-cover" loading="lazy" />
                  </button>
                  {(g.caption || g.event_title) && <figcaption className="bg-parchment-card p-3 text-sm"><span className="font-semibold text-crimson">{g.event_title}</span>{g.event_title && g.caption ? " — " : ""}{g.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        ))}
      </Section>
      <Lightbox items={lightbox?.items || []} index={lightbox ? lightbox.index : null} onClose={() => setLightbox(null)} onIndex={(i) => setLightbox((l) => ({ ...l, index: i }))} src={src} />
    </div>
  );
}

export function Constitution() {
  const { t } = useLang();
  const { user } = useAuth();
  const { data } = useApi(user ? "/documents" : null);
  if (!user) return (
    <div data-testid="constitution-page">
      <PageHero eyebrow="Governing Documents" title="Constitution & By-Laws"
        subtitle="Our constitution, by-laws, minutes and reports — the framework of our unity." />
      <Section>
        <div className="glass mx-auto max-w-xl rounded-2xl p-8 text-center" data-testid="constitution-gate">
          <h3 className="font-serif-display text-2xl font-bold text-crimson">{t("Members can view the constitution")}</h3>
          <p className="mt-2 text-muted-foreground">{t("Sign in to read the constitution and by-laws, or apply to become a member of the association.")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/login" state={{ from: "/constitution" }} className="rounded-full bg-crimson px-6 py-2.5 font-semibold text-parchment hover:bg-crimson-hover" data-testid="constitution-signin">{t("Sign in")}</Link>
            <Link to="/registration" className="rounded-full border border-gold px-6 py-2.5 font-semibold text-crimson hover:bg-gold-light" data-testid="constitution-join">{t("Become a member")}</Link>
          </div>
        </div>
      </Section>
    </div>
  );
  return (
    <div data-testid="constitution-page">
      <PageHero eyebrow="Governing Documents" title="Constitution & By-Laws"
        subtitle="Our constitution, by-laws, minutes and reports — the framework of our unity." />
      <Section>
        <div className="space-y-3">
          {(data || []).filter((d) => d.public !== false).map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-xl border border-gold/30 bg-parchment-card p-5" data-testid={`doc-${d.id}`}>
              <div>
                <span className="eyebrow text-gold">{d.category}</span>
                <h3 className="font-serif-display text-lg font-bold text-crimson">{d.title}</h3>
                <p className="text-sm text-muted-foreground">{d.description}</p>
              </div>
              <a href={d.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${d.url}` : d.url} target="_blank" rel="noreferrer" className="rounded-lg bg-crimson px-4 py-2 text-sm font-semibold text-parchment hover:bg-crimson-hover" data-testid={`doc-download-${d.id}`}>{t("Download PDF")}</a>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
